import React from 'react';
import { observer } from 'mobx-react';
import { FeedEntryWrapper } from '@seedlang/hoc';
import { WebUserInputStore } from '@seedlang/stores';
import AudioPlayer from 'components/media/audio_player';
import VideoPlayer from 'components/media/video_player';
import { AppUI } from '@seedlang/state';
import { isPresent, pixify } from '@seedlang/utils';
import { ThreeDotsIcon } from '@seedlang/icons';
import Dropdown from 'components/dropdown';
import CompositeVideoPlayer from 'components/media/composite_video_player';
import styled from '@emotion/styled';
import { Theme } from '@seedlang/constants';
import Text from 'components/text';
import { flexCenter } from '@seedlang/style_mixins';

const Wrapper = styled.div`
  margin-top: 10px;
  li {
    padding: 10px;
    color: #060606;
    font-size: 13px;
  }
  li:hover {
    background: $darkestBlue;
  }
  .three-dots-icon {
    width: 15px;
    display: flex;
    justify-content: flex-end;
    height: 18px;
    margin-top: 5px;
    top: -7px;
    svg {
      width: 5px;
    }
  }
`;

const Card = styled.div`
  border: ${props => props.private ? `2px solid ${Theme.orange}` : 'none'};
  background: white;
  padding: 10px;
  border-radius: 5px;
  overflow: hidden;
  position: relative;
  width: 100%;
  box-shadow: 0 2px 2px 0 rgba(0,0,0,0.14), 0 1px 5px 0 rgba(0,0,0,0.12), 0 3px 1px -2px rgba(0,0,0,0.2);
`;

const Title = styled.div`
  padding: 5px 20px 10px 0;
  display: flex;
  font-weight: bold;
  font-size: 21px;
  line-height: 22px;
  flex: 1;
`;

const VideoSection = styled.div`
  margin: 0 auto;
  display: flex;
  justify-content: center;
  border-top-right-radius: 5px;
  border-top-left-radius: 5px;
`;

const VideoPlayerWrapper = styled.div`
  position: relative;
  width: ${props => props.width};
  height: ${props => props.height};
  .video-player {
    left: 0px;
    width: 100%;
    height: 100%;
  }
  .supplement {
    .video-player {
      border-top-right-radius: 5px;
      border-top-left-radius: 5px;
      overflow: hidden;
    }
  }
  .media-with-supplement, .video-player {
    border-top-right-radius: 5px;
    border-top-left-radius: 5px;
  }
  .composite-video-player {
    .quadrant {
      background: black;
    }
  }
`;

const UserSection = styled.div`
  padding: 10px;
`;

const Footer = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 0 10px 10px 10px;
`;

const PrivateLabel = styled.div`
  ${flexCenter()}
  width: fit-content;
  background: ${Theme.orange};
  cursor: pointer;
  height: 30px;
  padding: 3px 6px;
  margin-right: 5px;
  color: white;
  font-size: 12px;
  border-radius: 3px;
`;

const Date = styled.div`
  color: #999;
  flex: 1;
  font-size: 12px;
  line-height: 14px;
  text-align: right;
`;

@observer
class FeedEntry extends React.Component {
  userInputStore = new WebUserInputStore();

  componentWillUnmount() {
    this.userInputStore.dispose();
  }

  render() {
    return (
      <Wrapper>
        <Card
          private={this.props.feedEntry.private}
        >
          {
            (this.props.submittedByMe || AppUI.user.admin) &&
              <Dropdown
                backgroundOnHover={Theme.lightBlue}
              >
                <ThreeDotsIcon
                  className='main-three-dots'
                />
                <ul>
                  <li
                    onClick={this.props.onTogglePrivate}
                  >
                    {this.props.feedEntry.private ? 'Make Public' : 'Make Private'}
                  </li>
                  <li
                    onClick={() => this.props.onDestroyFeedEntry(this.props.feedEntry.id)}
                  >
                    Delete
                  </li>
                </ul>
              </Dropdown>
          }
          <Title>
            {this.props.feedEntry.concept.name}
          </Title>
          <VideoSection>
            <VideoPlayerWrapper
              width={pixify(this.props.width - 20)}
              height={pixify(this.props.width - 20)}
            >
              {
                this.props.feedEntry.hasCompositeMedia &&
                  <CompositeVideoPlayer
                    height={this.props.width - 20}
                    width={this.props.width - 20}
                    mediaItems={this.props.feedEntry.feedEntryMediaItems}
                  />
              }
              {
                !this.props.feedEntry.hasCompositeMedia &&
                  <VideoPlayer
                    autoPlay={this.props.feedEntry.loopSupplement}
                    loop={this.props.feedEntry.loopSupplement}
                    videoClip={this.props.feedEntry.videoClip}
                    usingDataSaverHeader={AppUI.usingDataSaverHeader}
                    width={this.props.width - 20}
                  />
              }
            </VideoPlayerWrapper>
          </VideoSection>
          <UserSection>
            {
              this.props.feedEntry.audioUrl &&
                <AudioPlayer
                  margin='10px 0 0 0'
                  url={this.props.feedEntry.audioUrl}
                  duration={this.props.feedEntry.duration}
                  userInputStore={this.props.userInputStore || this.userInputStore}
                />
            }
            {
              isPresent(this.props.feedEntry.message) &&
                <Text
                  margin='10px 0 0 0'
                >
                  {this.props.feedEntry.message}
                </Text>
            }
          </UserSection>
          <Footer>
            {
              this.props.feedEntry.private &&
                <PrivateLabel>
                  Private
                </PrivateLabel>
            }
            <Date>
              {this.props.feedEntry.createdAt?.relativeTime}
            </Date>
          </Footer>
        </Card>
      </Wrapper>
    );
  }
}

export default FeedEntryWrapper(FeedEntry);
